import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import API from '../../../../services/API'
import DeleteIcon from '@mui/icons-material/Delete'
import { GlobalContext, UserContext } from '../../../../App'
import SmallModal from '../../../../components/Modals/SmallModal'
import StaffSelector from '../../../events/StaffSelector'
import EmployeeSelector from '../../../events/EmployeeSelector'

const SiteAdmins = () => {
  const [loading, setLoading] = useState(true)
  const [siteAdmins, setSiteAdmins] = useState([])
  const { user: currentUser } = useContext(UserContext)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const { setToast } = useContext(GlobalContext)

  const loadSiteAdmins = () => {
    setLoading(true)
    API.get('/admin/site-configuration/site-admins')
      .then((res) => res.status === 200 && setSiteAdmins(res.data))
      .catch(() => console.error('Unable to fetch'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadSiteAdmins()
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()
    API.post('/siteadmins/new', {
      userID: selectedEmployee._id,
      note: `${selectedEmployee.firstName} ${selectedEmployee.lastName} added as a Site Administrator.`,
    }).then((res) => {
      if (res.status === 200) {
        setToast({
          message: 'User Added',
        })
        loadSiteAdmins()
        setSelectedEmployee(null)
      } else {
        setToast({
          message: 'Unable to add user. Please try again later.',
          severity: 'error',
        })
      }
    })
  }

  const removeAccess = (user) => {
    API.post('/siteadmins/remove', {
      userID: user._id,
      note: `${user.firstName} ${user.lastName} removed as a Site Administrator.`,
    }).then((res) => {
      if (res.status === 200) {
        setToast({
          message: 'User Removed',
        })
        setConfirmDelete(null)
        loadSiteAdmins()
      } else {
        setToast({
          message: 'Unable to remove user. Please try again later.',
          severity: 'error',
        })
      }
    })
  }

  return (
    <Stack gap={2}>
      <Typography variant="h6" fontWeight={600}>
        Site Administrators
      </Typography>

      <Alert severity="info" sx={{ borderRadius: 2 }}>
        These users have administrator access to your AmbuNet instance.
      </Alert>

      <Stack gap={1.5}>
        {siteAdmins.map((user, index) => (
          <Card
            key={user._id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              // Long emails otherwise push the owner chip past the viewport
              // edge on phones — let the row wrap instead
              flexWrap: 'wrap',
              gap: 1,
              p: 2,
              borderRadius: 3,
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
              transition: '0.2s',
              '&:hover': {
                boxShadow: '0 4px 10px rgba(0,0,0,0.4)',
              },
            }}
          >
            <Stack direction="row" alignItems="center" gap={2} minWidth={0}>
              <Avatar
                src={user.photograph}
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                }}
              />
              <Box minWidth={0}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {user.firstName} {user.lastName}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.25, overflowWrap: 'anywhere' }}
                >
                  {user.email}
                </Typography>
              </Box>
            </Stack>

            {/* Delete button */}
            <Stack direction="row" alignItems="center">
              {index >= 1 &&
                user._id !== currentUser._id &&
                !user?.security?.isTenantOwner && (
                  <IconButton
                    color="error"
                    onClick={() => setConfirmDelete(user)}
                    // disabled={currentUser._id === user._id}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              {user?.security?.isTenantOwner ? (
                <Chip
                  color="anPurple"
                  label="Account Owner"
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: 1 }}
                />
              ) : (
                <div />
              )}
            </Stack>
          </Card>
        ))}
      </Stack>
      <Dialog open={!!confirmDelete}>
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          Remove Site Administrator
        </DialogTitle>
        <DialogContent dividers sx={{ borderBottom: 'none' }}>
          <Stack direction="row" alignItems="center" gap={2} sx={{ mb: 2 }}>
            <Avatar
              src={confirmDelete?.photograph}
              sx={{ width: 48, height: 48, borderRadius: 2 }}
            />
            <Stack>
              <Typography variant="subtitle1" fontWeight={600}>
                {confirmDelete?.firstName} {confirmDelete?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {confirmDelete?.email}
              </Typography>
            </Stack>
          </Stack>

          <Typography variant="body1">
            Are you sure you want to remove this user as a Site Administrator?
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            They will immediately lose access to all system configuration
            settings.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setConfirmDelete(null)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => removeAccess(confirmDelete)}
            variant="contained"
            color="error"
          >
            Remove Admin
          </Button>
        </DialogActions>
      </Dialog>
      <Stack padding={1} spacing={1} component="form" onSubmit={handleSubmit}>
        <Typography variant="subtitle1">
          Add a user to your Site Admin
        </Typography>
        <Typography>
          This will provide users with access to the management of your AmbuNet
          account, including billing and subscriptions.{' '}
        </Typography>
        <Stack
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={1}
          display="flex"
        >
          <EmployeeSelector
            value={selectedEmployee}
            onChange={(e, newValue) => setSelectedEmployee(newValue)}
            preventSelectingSelf
            label="Employee"
            fullWidth
            required
          />
          <Button variant="contained" color="success" type="submit">
            Confirm
          </Button>
        </Stack>
        {selectedEmployee && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            {selectedEmployee.firstName} {selectedEmployee.lastName} will be
            granted full Site Administrator access, including system
            configuration, user management, billing and subscriptions. Only
            grant this to staff you trust with organisation-wide settings.
          </Alert>
        )}
      </Stack>
    </Stack>
  )
}

export default SiteAdmins
